import React, {useEffect, useState} from "react"
import {useTranslation} from "react-i18next"
import {ItemRenderer, Select} from "@blueprintjs/select"
import {MenuItem, PopoverPosition} from "@blueprintjs/core"
import {Tooltip2} from "@blueprintjs/popover2"
import {useDispatch, useSelector} from "react-redux"
import Props from "../../../core/components/props"
import MinimalButton from "../../../core/components/buttons/MinimalButton"
import {
    loadMaterialsAction,
    loadMatricesAction,
} from "../../redux/refdb.actions"
import {
    getContext,
    getMatrices,
    materialsSelector,
} from "../../redux/refdb.selector"
import Matrix from "../../models/matrix"
import useComponentWillMount from "../../../core/utils/componentUtils"

const Combobox = Select.ofType<Matrix>()

export interface ValueTypeSelectProps extends Props {
    selectedValue: Matrix
    setSelectedValue: (value: Matrix) => void
    disabled?: boolean
}

const MatrixTypeSelect: React.FC<ValueTypeSelectProps> = (props) => {
    const {className, id, selectedValue, setSelectedValue, disabled} = props
    const dispatch = useDispatch()
    const {t} = useTranslation()
    const materials = useSelector(materialsSelector)
    useComponentWillMount(() => {
        dispatch(loadMaterialsAction(true))
        dispatch(loadMatricesAction(true))
    })
    const list = useSelector(getMatrices)
    const context = useSelector(getContext)
    const [label, setLabel] = useState<string>("Select your matrix")
    const [tooltip, setTooltip] = useState<boolean>(false)
    const toggleTooltip = (value) => setTooltip(value)
    const disableTooltip = (value) => {
        setTimeout(() => setTooltip(value), 150)
    }

    // Update label in case of settings template load
    useEffect(() => {
        if (context.matrixType?.label) {
            setSelectedValue(context.matrixType)
            setLabel(Matrix.matrixLabel(context.matrixType))
        }
    }, [context])

    const dataForMaterialNotExists = (valueType: Matrix) => {
        return !(materials || []).includes(valueType.material)
    }

    const isItemSelected = (item: Matrix) => {
        return selectedValue === item
    }

    const renderValueType: ItemRenderer<Matrix> = (
        valueType,
        {handleClick, modifiers, query}
    ) => {
        if (!modifiers.matchesPredicate) {
            return null
        }

        return (
            <MenuItem
                active={modifiers.active}
                disabled={
                    dataForMaterialNotExists(valueType) || !valueType.enabled
                }
                text={Matrix.matrixLabel(valueType)}
                key="key"
                onClick={handleClick}
                shouldDismissPopover={false}
                className={
                    isItemSelected(valueType) ? "biocrates-selected" : ""
                }
            />
        )
    }

    return (
        <div>
            <Combobox
                popoverProps={{
                    onOpening: () => {
                        toggleTooltip(true)
                    },
                    onClosing: () => {
                        disableTooltip(false)
                    },
                }}
                disabled={disabled}
                className={className}
                items={list}
                itemRenderer={renderValueType}
                onItemSelect={(valueType: Matrix) => {
                    setSelectedValue(valueType)
                    setLabel(Matrix.matrixLabel(valueType))
                    setTooltip(true)
                }}
                activeItem={selectedValue}
                filterable={false}
            >
                <Tooltip2
                    content={t("Set sample matrix")}
                    position={PopoverPosition.BOTTOM}
                    disabled={tooltip}
                >
                    <MinimalButton
                        rightIcon="caret-down"
                        text={label}
                        id={id}
                    />
                </Tooltip2>
            </Combobox>
        </div>
    )
}

export default MatrixTypeSelect
